/**
 * The admin route table: every page the sidebar links to, written once. The sidebar
 * (`buildNav`) and the header title (`pageTitle`) are both derived from it.
 */

/** Icon names map to lucide components in components/Sidebar.tsx (keeps this file pure). */
export type NavIcon =
  | 'home'
  | 'today'
  | 'review'
  | 'quality'
  | 'questions'
  | 'generation'
  | 'kb'
  | 'ask'
  | 'flashcards'
  | 'mocks'
  | 'grandMock'
  | 'packs'
  | 'students'
  | 'activity'
  | 'support'
  | 'growth'
  | 'earlyBird'
  | 'products'
  | 'flags'
  | 'ai'
  | 'users'
  | 'audit'
  | 'account';

/** Groups are organised by the job you are doing, not by how the system is built. */
export type NavGroupId = 'home' | 'questions' | 'exams' | 'students' | 'money' | 'settings';

/** Hash route segments, e.g. ['products', '<id>', 'mocks']. */
export type Route = readonly string[];

/**
 * Who sees the entry: everyone, holders of a global permission, or admins with the permission on
 * some readable product (the link then opens that product).
 */
export type RouteAccess =
  | { scope: 'everyone' }
  | { scope: 'global'; permission: string }
  | { scope: 'product'; permission: string };

export interface RouteDef {
  id: string;
  /** Sidebar group; the account entry sits outside the groups. */
  group: NavGroupId | 'account';
  label: string;
  icon: NavIcon;
  access: RouteAccess;
  /** Link target; product pages build it from the product id. */
  href: string | ((productId: string) => string);
  /** True when the route is this page (drives the highlight and the header title). */
  matches: (route: Route) => boolean;
  /** Header title when it differs from the label (detail and import pages). */
  title?: (route: Route) => string;
  /** Which open count, if any, is shown next to the label. */
  badge?: 'review' | 'support';
}

export const NAV_GROUPS: readonly { id: NavGroupId; label: string; hideLabel?: true }[] = [
  // Home has a single item, so its heading is only for screen readers.
  { id: 'home', label: 'Home', hideLabel: true },
  { id: 'questions', label: 'Questions' },
  { id: 'exams', label: 'Exams & mocks' },
  { id: 'students', label: 'Students' },
  { id: 'money', label: 'Money' },
  { id: 'settings', label: 'Settings' },
];

/** Product tabs that have their own sidebar entry (the rest stay under Settings → Products). */
const PRODUCT_TABS = {
  mocks: 'mock.read',
  growth: 'growth.read',
  packs: 'pack.read',
  flags: 'flag.read',
} as const;
type ProductTab = keyof typeof PRODUCT_TABS;

const isProductTab = (tab: string | undefined): tab is ProductTab =>
  tab !== undefined && Object.hasOwn(PRODUCT_TABS, tab);

/** A product page that has its own sidebar entry: `#/products/<id>/<tab>`. */
function productTab(tab: ProductTab, group: NavGroupId, label: string, icon: NavIcon): RouteDef {
  return {
    id: tab,
    group,
    label,
    icon,
    access: { scope: 'product', permission: PRODUCT_TABS[tab] },
    href: (productId) => `#/products/${productId}/${tab}`,
    matches: ([section, sub, page]) => section === 'products' && !!sub && page === tab,
  };
}

/** A top-level page at `#/<id>` whose route segment is its id. */
function section(
  id: string,
  group: RouteDef['group'],
  label: string,
  icon: NavIcon,
  access: RouteAccess,
): RouteDef {
  return { id, group, label, icon, access, href: `#/${id}`, matches: ([s]) => s === id };
}

const ITEM_READ: RouteAccess = { scope: 'global', permission: 'item.read' };
const KB_READ: RouteAccess = { scope: 'global', permission: 'kb.read' };
const EVERYONE: RouteAccess = { scope: 'everyone' };

/** Every admin page in sidebar order. */
export const ROUTES: readonly RouteDef[] = [
  {
    id: 'home',
    group: 'home',
    label: 'Home',
    icon: 'home',
    access: EVERYONE,
    href: '#/',
    matches: (route) => route.length === 0 || route[0] === 'home',
  },
  section('today', 'questions', 'Today', 'today', ITEM_READ),
  { ...section('review', 'questions', 'Review', 'review', ITEM_READ), badge: 'review' },
  section('quality', 'questions', 'Quality', 'quality', ITEM_READ),
  {
    id: 'questions',
    group: 'questions',
    label: 'Question bank',
    icon: 'questions',
    access: ITEM_READ,
    href: '#/studio',
    matches: ([s]) => s === 'studio' || s === 'import' || s === 'pyq-import',
    title: ([s, sub]) => {
      if (s === 'import') return 'Import questions';
      if (s === 'pyq-import') return 'SSC paper import';
      return sub && sub !== 'job' ? 'Question' : 'Question bank';
    },
  },
  section('generation', 'questions', 'Generation', 'generation', ITEM_READ),
  {
    ...section('kb', 'questions', 'Knowledge base', 'kb', KB_READ),
    matches: ([s, sub]) => s === 'kb' && sub !== 'ask',
    title: ([, sub, id]) => (sub === 'sources' && id ? 'Source' : 'Knowledge base'),
  },
  {
    id: 'ask',
    group: 'questions',
    label: 'Search & ask',
    icon: 'ask',
    access: KB_READ,
    href: '#/kb/ask',
    matches: ([s, sub]) => s === 'kb' && sub === 'ask',
  },
  section('flashcards', 'questions', 'Flashcards', 'flashcards', ITEM_READ),
  {
    ...section('grand-mock', 'exams', 'SSC Rankers League', 'grandMock', {
      scope: 'product',
      permission: 'grand_mock.read',
    }),
    title: ([, sub]) => (sub === 'new' ? 'New SSC Rankers League' : 'SSC Rankers League'),
  },
  productTab('mocks', 'exams', 'Mocks', 'mocks'),
  productTab('packs', 'exams', 'Exam packs', 'packs'),
  {
    ...section('students', 'students', 'All students', 'students', {
      scope: 'product',
      permission: 'students.read',
    }),
    title: ([, sub]) => (sub ? 'Student' : 'All students'),
  },
  section('activity', 'students', 'Activity', 'activity', {
    scope: 'product',
    permission: 'students.read',
  }),
  {
    ...section('support', 'students', 'Support', 'support', {
      scope: 'product',
      permission: 'support.read',
    }),
    badge: 'support',
  },
  productTab('growth', 'students', 'Growth & current affairs', 'growth'),
  section('early-bird', 'money', 'Early-bird offer', 'earlyBird', {
    scope: 'product',
    permission: 'commerce.read',
  }),
  {
    ...section('products', 'settings', 'Products', 'products', EVERYONE),
    // Product tabs with their own entry are titled by that entry.
    matches: ([s, sub, tab]) => s === 'products' && !(sub && isProductTab(tab)),
    title: ([, sub]) => (sub ? 'Product' : 'Products'),
  },
  productTab('flags', 'settings', 'Feature switches', 'flags'),
  section('ai', 'settings', 'AI console', 'ai', { scope: 'global', permission: 'ai.read' }),
  section('users', 'settings', 'Admins & roles', 'users', {
    scope: 'global',
    permission: 'admin_user.read',
  }),
  section('audit', 'settings', 'Audit log', 'audit', { scope: 'global', permission: 'audit.read' }),
  section('account', 'account', 'My account', 'account', EVERYONE),
];

/** True for the sidebar entries that open a product tab (see `PRODUCT_TABS`). */
export const opensProductTab = (route: RouteDef): boolean =>
  route.access.scope === 'product' && isProductTab(route.id);
